import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import FirmarDocumento from '../Sidebar/CustomerComponents/FirmarDocumento';
import { scanDni } from '../../lib/dniScanner';
import { fusionarEscaneoDni } from '../../lib/fusionarEscaneoDni';
import { getImageUrl } from '../../lib/helpers';
import {
  compressImageConTope, prepareDniPhotosConTope, dataUrlABlob, MAX_IMAGE_PX, IMAGE_QUALITY,
} from '../../lib/imagenesPreinscripcion';
import usePreinscripcionSubidas, { KINDS, TIEMPOS } from './usePreinscripcionSubidas';
import { Camera, CheckCircle2, LinkIcon, ScanLine, Upload, UserRound } from 'lucide-react';
import './PreRegisterPage.css';
import flagEs from './flags/es.svg';
import flagEn from './flags/en.svg';
import flagIt from './flags/it.svg';
import flagDe from './flags/de.svg';
import flagPt from './flags/pt.svg';
import flagFr from './flags/fr.svg';
import flagNl from './flags/nl.svg';

// ── helpers ──────────────────────────────────────────────────────────────────
function toISO(display) {
  if (!display) return '';
  const [d, m, y] = display.split('/');
  return `${y}-${m}-${d}`;
}
function toDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtDate(v) {
  const nums = v.replace(/\D/g, '');
  let r = '';
  for (let i = 0; i < Math.min(nums.length, 8); i++) {
    if (i === 2 || i === 4) r += '/';
    r += nums[i];
  }
  return r;
}

// ── i18n: formulario público en ES / EN / IT / DE / PT / FR / NL ─────────────
// Las banderas son SVG en ficheros, nunca emoji: los emoji de bandera no tienen
// glifo en muchos Android y salían como cuadros vacíos (decidido 02-09-2026).
const LANGS = [
  { code: 'es', label: 'Español',    flag: flagEs },
  { code: 'en', label: 'English',    flag: flagEn },
  { code: 'it', label: 'Italiano',   flag: flagIt },
  { code: 'de', label: 'Deutsch',    flag: flagDe },
  { code: 'pt', label: 'Português',  flag: flagPt },
  { code: 'fr', label: 'Français',   flag: flagFr },
  { code: 'nl', label: 'Nederlands', flag: flagNl },
];

// Timeouts de la petición pequeña: el envío final es un JSON de unos pocos KB (las
// imágenes ya van subidas), así que 20 s sobran y un móvil sin cobertura sale pronto al
// reintento en vez de quedarse 2 minutos esperando.
const SOLICITUD_TIMEOUT_MS = 20000;
const ESTADO_TIMEOUT_MS = 10000;
// Tras un envío sin respuesta: se pregunta al servidor y, si no llegó, se reenvía con la
// MISMA clave hasta 3 veces (esperas de base, 2×base y 4×base).
const MAX_REENVIOS = 3;

// Every attempt from this page carries the same key, so a submit the server already
// persisted — but whose response we never saw — comes back as that same record instead
// of a second one. Desde RF-023 la clave SÍ sobrevive a una recarga (va en el borrador,
// que caduca a los 30 min): sin eso, recargar con la solicitud en el aire creaba un
// duplicado. La tablet compartida (#330) se protege en el borrador: una solicitud con
// fotos o ya intentada nunca se reanuda en silencio, ver el aviso «Continuar / Empezar
// de nuevo».
// `uuid` is not a declared dependency of this app; `crypto.randomUUID` needs a secure
// context, which the pre-registration form always has (it handles ID documents).
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Esperas con salida (RF-025): ninguna petición del formulario público gira sin tope.
const INFO_TIMEOUT_MS = 15000;
const ACTA_TIMEOUT_MS = 30000;

export const STRINGS = {
  es: {
    abrirAparte: 'Abrir el documento en otra pestaña', heLeido: 'He leído el documento y estoy de acuerdo', faltaLeer: 'Falta marcar «He leído el documento» para poder firmar.', faltaFirmar: 'Falta tu firma en el recuadro.', irAFirmar: 'Ir a firmar ↓', avisoMovil: 'Si el documento no se ve aquí, ábrelo en otra pestaña con el enlace de arriba y vuelve para firmar: la firma se hace en el recuadro de abajo, no sobre el PDF.', descargarDocumento: 'Descargar el documento para leerlo', avisoSinVisor: 'Este navegador no puede mostrar el PDF aquí. Descárgalo, léelo desde la notificación de descarga y vuelve: la firma se hace en el recuadro de abajo, no sobre el PDF.',
    actaTitulo: 'Acta de inscripción', firmar: 'Firmar documento', firmado: '✓ Documento firmado', verOFirmar: 'Ver o volver a firmar', actaSub: 'Lee el documento y firma al final.', actaCargando: 'Cargando el documento…', actaError: 'No se pudo cargar el documento. Sin documento no se puede firmar.', tuFirma: 'Tu firma', firmaAyuda: 'Firma con el dedo dentro del recuadro.', firmarYCerrar: 'Firmar y cerrar', cerrarSinFirmar: 'Cerrar sin firmar',
    linkInvalid: 'Enlace no válido',
    linkInvalidDesc: 'Este enlace de preinscripción no existe o ha expirado.',
    sentTitle: '¡Solicitud enviada!',
    sentDesc: (name) => `Tu solicitud de preinscripción ha sido recibida por ${name}. El club la revisará y se pondrá en contacto contigo.`,
    subtitle: 'Formulario de preinscripción de socio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto del socio', capture: 'Capturar', cancel: 'Cancelar', takeSelfie: 'Hacer selfie', uploadPhoto: 'Subir foto', retry: 'Repetir',
    scanDni: 'Escanear DNI (opcional — rellena el formulario automáticamente)', front: 'Anverso', back: 'Reverso', dniReadError: 'No se pudo leer el DNI.',
    scanDniRequired: 'Escanear DNI * (obligatorio — rellena el formulario automáticamente)', photoRequiredError: 'Este club requiere la foto del socio para preinscribirse.', dniRequiredError: 'Este club requiere el escaneo del documento (al menos el anverso).', dniSaveFailedError: 'No se pudo guardar el documento. Inténtalo de nuevo.',
    scanFilled: (fields) => `Rellenado con el documento: ${fields}. Revísalo antes de enviar.`,
    scanKept: 'No hemos tocado lo que ya habías escrito. El documento dice:', scanUse: 'Usar',
    firstName: 'Nombre *', lastName: 'Apellidos *', dniNie: 'DNI / NIE', birthDate: 'Fecha nacimiento', birthPlaceholder: 'dd/mm/aaaa', phone: 'Teléfono', email: 'Email', address: 'Dirección', city: 'Ciudad', postalCode: 'Código postal',
    requiredError: 'Nombre y apellidos son obligatorios.', submitError: 'Error al enviar la solicitud. Inténtalo de nuevo.', submitting: 'Enviando…', submit: 'Enviar solicitud',
    camHttps: 'La cámara solo está disponible en conexiones seguras (HTTPS). Usa "Subir foto" para seleccionar una imagen.',
    camNoSupport: 'Tu navegador no permite acceder a la cámara. Usa "Subir foto" para seleccionar una imagen.',
    camDenied: 'No se pudo acceder a la cámara. Comprueba que el navegador tiene permiso.',
    camHttp: 'La cámara no está disponible en HTTP. Usa "Subir foto" para seleccionar una imagen.',
    cameraNotReady: 'La cámara aún no está lista. Espera un segundo y vuelve a intentarlo.',
    cameraCaptureFailed: 'No se pudo guardar la foto. Inténtalo otra vez o usa "Subir foto".',
    crashTitle: 'Se ha producido un error',
    crashDesc: 'Hemos guardado los datos que habías escrito. Vuelve a intentarlo: si ya habías hecho fotos, tendrás que repetirlas.',
    crashRetry: 'Reintentar',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'No se pudo leer el documento. Rellena los datos a mano.', infoErrorTitle: 'No se pudo cargar el formulario', infoErrorDesc: 'Comprueba tu conexión e inténtalo de nuevo.', reintentar: 'Reintentar',
    esperandoLectura: 'Esperando a que termine la lectura del documento…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Subiendo… ${p}%`, subidaOk: '✓ Subida', cambiar: 'Cambiar',
    esperandoSubida: { foto: 'Esperando a que se suba la foto del socio…', dni_anverso: 'Esperando a que se suba el anverso del documento…', dni_reverso: 'Esperando a que se suba el reverso del documento…', firma: 'Esperando a que se suba la firma…' },
    errSubida: { red: 'No se pudo subir. Comprueba tu conexión y toca Reintentar.', tipo: 'Este archivo no es una imagen válida. Haz o elige otra.', grande: 'La foto pesa demasiado. Haz o elige otra.', vacio: 'El archivo está vacío. Haz o elige otra foto.', sinArchivo: 'Esta foto hay que hacerla otra vez.', otro: 'No se pudo subir. Haz o elige otra foto.', firma: 'No se pudo guardar la firma. Vuelve a firmar.' },
    revisaCampo: (c) => `Revisa el campo ${c}`, quitar: 'Quitar',
    bloqueoReintentar: 'Una foto no se ha podido subir. Toca «Reintentar».', bloqueoRehacer: 'Una imagen no se ha podido subir. Vuelve a hacer la foto.',
    bloqueoRehacerOQuitar: 'Una imagen no se ha podido subir. Vuelve a hacer la foto o quítala.',
    envioGrande: 'El envío es demasiado grande. Vuelve a intentarlo.', envioReintentar: 'El envío no se pudo completar. Vuelve a intentarlo.', borradorElige: 'Elige una opción para seguir.',
    noHaLlegado: 'Tu solicitud no ha llegado. Comprueba tu conexión y vuelve a intentarlo.', reintentandoEnvio: 'Sin respuesta del servidor. Comprobando y reintentando…', fotoCaducada: 'Una de las fotos ya no es válida. Vuelve a hacerla y envía de nuevo.',
    borradorAviso: 'Hay una solicitud sin terminar en este dispositivo.', continuarDe: (n) => (n ? `Continuar la solicitud de ${n}` : 'Continuar la solicitud'), empezarNuevo: 'Empezar de nuevo',
  },
  en: {
    abrirAparte: 'Open the document in a new tab', heLeido: 'I have read the document and I agree', faltaLeer: 'Tick «I have read the document» to be able to sign.', faltaFirmar: 'Your signature is missing from the box.', irAFirmar: 'Go to sign ↓', avisoMovil: 'If the document does not show here, open it in a new tab with the link above and come back to sign: you sign in the box below, not on the PDF.', descargarDocumento: 'Download the document to read it', avisoSinVisor: 'This browser cannot show the PDF here. Download it, read it from the download notification and come back: you sign in the box below, not on the PDF.',
    actaTitulo: 'Membership agreement', firmar: 'Sign document', firmado: '✓ Document signed', verOFirmar: 'View or sign again', actaSub: 'Read the document and sign at the end.', actaCargando: 'Loading the document…', actaError: 'The document could not be loaded. You cannot sign without it.', tuFirma: 'Your signature', firmaAyuda: 'Sign with your finger inside the box.', firmarYCerrar: 'Sign and close', cerrarSinFirmar: 'Close without signing',
    linkInvalid: 'Invalid link',
    linkInvalidDesc: 'This pre-registration link does not exist or has expired.',
    sentTitle: 'Request sent!',
    sentDesc: (name) => `Your pre-registration request has been received by ${name}. The club will review it and get in touch with you.`,
    subtitle: 'Member pre-registration form',
    poweredBy: 'Powered by',
    photoLabel: 'Member photo', capture: 'Capture', cancel: 'Cancel', takeSelfie: 'Take selfie', uploadPhoto: 'Upload photo', retry: 'Retake',
    scanDni: 'Scan ID (optional — fills in the form automatically)', front: 'Front', back: 'Back', dniReadError: 'Could not read the ID.',
    scanDniRequired: 'Scan ID * (required — fills in the form automatically)', photoRequiredError: 'This club requires a member photo to pre-register.', dniRequiredError: 'This club requires a scan of your ID (at least the front side).', dniSaveFailedError: 'Your document could not be saved. Please try again.',
    scanFilled: (fields) => `Filled in from your ID: ${fields}. Please check it before sending.`,
    scanKept: 'We kept what you had already typed. Your ID says:', scanUse: 'Use',
    firstName: 'First name *', lastName: 'Last name *', dniNie: 'ID / passport', birthDate: 'Date of birth', birthPlaceholder: 'dd/mm/yyyy', phone: 'Phone', email: 'Email', address: 'Address', city: 'City', postalCode: 'Postcode',
    requiredError: 'First name and last name are required.', submitError: 'Error sending the request. Please try again.', submitting: 'Sending…', submit: 'Send request',
    camHttps: 'The camera is only available over secure connections (HTTPS). Use "Upload photo" to select an image.',
    camNoSupport: 'Your browser does not allow camera access. Use "Upload photo" to select an image.',
    camDenied: 'Could not access the camera. Check that the browser has permission.',
    camHttp: 'The camera is not available over HTTP. Use "Upload photo" to select an image.',
    cameraNotReady: 'The camera is not ready yet. Wait a second and try again.',
    cameraCaptureFailed: 'The photo could not be saved. Try again or use "Upload photo".',
    crashTitle: 'Something went wrong',
    crashDesc: 'We saved the details you had typed. Please try again — any photos you had taken will need to be retaken.',
    crashRetry: 'Try again',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Could not read the document. Please fill in the details by hand.', infoErrorTitle: 'The form could not be loaded', infoErrorDesc: 'Check your connection and try again.', reintentar: 'Try again',
    esperandoLectura: 'Waiting for the document reading to finish…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Uploading… ${p}%`, subidaOk: '✓ Uploaded', cambiar: 'Change',
    esperandoSubida: { foto: 'Waiting for the member photo to upload…', dni_anverso: 'Waiting for the front of the document to upload…', dni_reverso: 'Waiting for the back of the document to upload…', firma: 'Waiting for the signature to upload…' },
    errSubida: { red: 'Could not upload. Check your connection and tap Try again.', tipo: 'This file is not a valid image. Take or choose another one.', grande: 'The photo is too large. Take or choose another one.', vacio: 'The file is empty. Take or choose another photo.', sinArchivo: 'This photo needs to be taken again.', otro: 'Could not upload. Take or choose another photo.', firma: 'The signature could not be saved. Please sign again.' },
    revisaCampo: (c) => `Check the field ${c}`, quitar: 'Remove',
    bloqueoReintentar: 'A photo could not be uploaded. Tap “Try again”.', bloqueoRehacer: 'An image could not be uploaded. Take the photo again.',
    bloqueoRehacerOQuitar: 'An image could not be uploaded. Take the photo again or remove it.',
    envioGrande: 'The submission is too large. Please try again.', envioReintentar: 'The submission could not be completed. Please try again.', borradorElige: 'Choose an option to continue.',
    noHaLlegado: 'Your request did not arrive. Check your connection and try again.', reintentandoEnvio: 'No reply from the server. Checking and trying again…', fotoCaducada: 'One of the photos is no longer valid. Take it again and send again.',
    borradorAviso: 'There is an unfinished request on this device.', continuarDe: (n) => (n ? `Continue the request of ${n}` : 'Continue the request'), empezarNuevo: 'Start over',
  },
  it: {
    abrirAparte: 'Apri il documento in una nuova scheda', heLeido: 'Ho letto il documento e sono d\'accordo', faltaLeer: 'Spunta «Ho letto il documento» per poter firmare.', faltaFirmar: 'Manca la tua firma nel riquadro.', irAFirmar: 'Vai a firmare ↓', avisoMovil: 'Se il documento non si vede qui, aprilo in una nuova scheda con il link sopra e torna per firmare: la firma si fa nel riquadro qui sotto, non sul PDF.', descargarDocumento: 'Scarica il documento per leggerlo', avisoSinVisor: 'Questo browser non può mostrare il PDF qui. Scaricalo, leggilo dalla notifica di download e torna: la firma si fa nel riquadro qui sotto, non sul PDF.',
    actaTitulo: 'Atto di iscrizione', firmar: 'Firma il documento', firmado: '✓ Documento firmato', verOFirmar: 'Vedi o firma di nuovo', actaSub: 'Leggi il documento e firma alla fine.', actaCargando: 'Caricamento del documento…', actaError: 'Impossibile caricare il documento. Senza documento non si può firmare.', tuFirma: 'La tua firma', firmaAyuda: 'Firma con il dito dentro il riquadro.', firmarYCerrar: 'Firma e chiudi', cerrarSinFirmar: 'Chiudi senza firmare',
    linkInvalid: 'Link non valido',
    linkInvalidDesc: 'Questo link di preiscrizione non esiste o è scaduto.',
    sentTitle: 'Richiesta inviata!',
    sentDesc: (name) => `La tua richiesta di preiscrizione è stata ricevuta da ${name}. Il club la esaminerà e ti contatterà.`,
    subtitle: 'Modulo di preiscrizione socio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto del socio', capture: 'Scatta', cancel: 'Annulla', takeSelfie: 'Fai un selfie', uploadPhoto: 'Carica foto', retry: 'Riprova',
    scanDni: 'Scansiona il documento (opzionale — compila il modulo automaticamente)', front: 'Fronte', back: 'Retro', dniReadError: 'Impossibile leggere il documento.',
    scanDniRequired: 'Scansiona il documento * (obbligatorio — compila il modulo automaticamente)', photoRequiredError: 'Questo club richiede la foto del socio per la preiscrizione.', dniRequiredError: 'Questo club richiede la scansione del documento (almeno il fronte).', dniSaveFailedError: 'Impossibile salvare il documento. Riprova.',
    scanFilled: (fields) => `Compilato dal documento: ${fields}. Controlla prima di inviare.`,
    scanKept: 'Non abbiamo modificato ciò che avevi già scritto. Il documento dice:', scanUse: 'Usa',
    firstName: 'Nome *', lastName: 'Cognome *', dniNie: 'Documento / passaporto', birthDate: 'Data di nascita', birthPlaceholder: 'gg/mm/aaaa', phone: 'Telefono', email: 'Email', address: 'Indirizzo', city: 'Città', postalCode: 'CAP',
    requiredError: 'Nome e cognome sono obbligatori.', submitError: "Errore nell'invio della richiesta. Riprova.", submitting: 'Invio…', submit: 'Invia richiesta',
    camHttps: 'La fotocamera è disponibile solo su connessioni sicure (HTTPS). Usa "Carica foto" per selezionare un\'immagine.',
    camNoSupport: 'Il tuo browser non consente l\'accesso alla fotocamera. Usa "Carica foto" per selezionare un\'immagine.',
    camDenied: 'Impossibile accedere alla fotocamera. Verifica che il browser abbia il permesso.',
    camHttp: 'La fotocamera non è disponibile su HTTP. Usa "Carica foto" per selezionare un\'immagine.',
    cameraNotReady: 'La fotocamera non è ancora pronta. Attendi un secondo e riprova.',
    cameraCaptureFailed: 'Impossibile salvare la foto. Riprova o usa "Carica foto".',
    crashTitle: 'Si è verificato un errore',
    crashDesc: 'Abbiamo salvato i dati che avevi inserito. Riprova: le foto già scattate dovranno essere rifatte.',
    crashRetry: 'Riprova',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Impossibile leggere il documento. Compila i dati a mano.', infoErrorTitle: 'Impossibile caricare il modulo', infoErrorDesc: 'Controlla la connessione e riprova.', reintentar: 'Riprova',
    esperandoLectura: 'In attesa che finisca la lettura del documento…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Caricamento… ${p}%`, subidaOk: '✓ Caricata', cambiar: 'Cambia',
    esperandoSubida: { foto: 'In attesa del caricamento della foto del socio…', dni_anverso: 'In attesa del caricamento del fronte del documento…', dni_reverso: 'In attesa del caricamento del retro del documento…', firma: 'In attesa del caricamento della firma…' },
    errSubida: { red: 'Caricamento non riuscito. Controlla la connessione e tocca Riprova.', tipo: 'Questo file non è un\'immagine valida. Scatta o scegli un\'altra foto.', grande: 'La foto è troppo pesante. Scatta o scegli un\'altra foto.', vacio: 'Il file è vuoto. Scatta o scegli un\'altra foto.', sinArchivo: 'Questa foto va rifatta.', otro: 'Caricamento non riuscito. Scatta o scegli un\'altra foto.', firma: 'Impossibile salvare la firma. Firma di nuovo.' },
    revisaCampo: (c) => `Controlla il campo ${c}`, quitar: 'Rimuovi',
    bloqueoReintentar: 'Una foto non è stata caricata. Tocca «Riprova».', bloqueoRehacer: 'Un\'immagine non è stata caricata. Rifai la foto.',
    bloqueoRehacerOQuitar: 'Un\'immagine non è stata caricata. Rifai la foto o rimuovila.',
    envioGrande: 'L\'invio è troppo grande. Riprova.', envioReintentar: 'Non è stato possibile completare l\'invio. Riprova.', borradorElige: 'Scegli un\'opzione per continuare.',
    noHaLlegado: 'La tua richiesta non è arrivata. Controlla la connessione e riprova.', reintentandoEnvio: 'Nessuna risposta dal server. Verifico e riprovo…', fotoCaducada: 'Una delle foto non è più valida. Rifalla e invia di nuovo.',
    borradorAviso: 'C\'è una richiesta non terminata su questo dispositivo.', continuarDe: (n) => (n ? `Continua la richiesta di ${n}` : 'Continua la richiesta'), empezarNuevo: 'Ricomincia da capo',
  },
  de: {
    abrirAparte: 'Dokument in neuem Tab öffnen', heLeido: 'Ich habe das Dokument gelesen und bin einverstanden', faltaLeer: 'Hake «Ich habe das Dokument gelesen» an, um unterschreiben zu können.', faltaFirmar: 'Deine Unterschrift fehlt im Feld.', irAFirmar: 'Zum Unterschreiben ↓', avisoMovil: 'Wenn das Dokument hier nicht angezeigt wird, öffne es über den Link oben in einem neuen Tab und komm zum Unterschreiben zurück: unterschrieben wird im Feld unten, nicht auf dem PDF.', descargarDocumento: 'Dokument herunterladen und lesen', avisoSinVisor: 'Dieser Browser kann das PDF hier nicht anzeigen. Lade es herunter, lies es über die Download-Benachrichtigung und komm zurück: unterschrieben wird im Feld unten, nicht auf dem PDF.',
    actaTitulo: 'Aufnahmeantrag', firmar: 'Dokument unterschreiben', firmado: '✓ Dokument unterschrieben', verOFirmar: 'Ansehen oder neu unterschreiben', actaSub: 'Lies das Dokument und unterschreibe am Ende.', actaCargando: 'Dokument wird geladen…', actaError: 'Das Dokument konnte nicht geladen werden. Ohne Dokument kann nicht unterschrieben werden.', tuFirma: 'Deine Unterschrift', firmaAyuda: 'Unterschreibe mit dem Finger im Feld.', firmarYCerrar: 'Unterschreiben und schließen', cerrarSinFirmar: 'Ohne Unterschrift schließen',
    linkInvalid: 'Ungültiger Link',
    linkInvalidDesc: 'Dieser Voranmeldungslink existiert nicht oder ist abgelaufen.',
    sentTitle: 'Anfrage gesendet!',
    sentDesc: (name) => `Deine Voranmeldung wurde von ${name} empfangen. Der Club prüft sie und wird sich mit dir in Verbindung setzen.`,
    subtitle: 'Voranmeldeformular für Mitglieder',
    poweredBy: 'Powered by',
    photoLabel: 'Foto des Mitglieds', capture: 'Aufnehmen', cancel: 'Abbrechen', takeSelfie: 'Selfie machen', uploadPhoto: 'Foto hochladen', retry: 'Wiederholen',
    scanDni: 'Ausweis scannen (optional — füllt das Formular automatisch aus)', front: 'Vorderseite', back: 'Rückseite', dniReadError: 'Ausweis konnte nicht gelesen werden.',
    scanDniRequired: 'Ausweis scannen * (erforderlich — füllt das Formular automatisch aus)', photoRequiredError: 'Dieser Club verlangt ein Mitgliedsfoto für die Voranmeldung.', dniRequiredError: 'Dieser Club verlangt einen Scan deines Ausweises (mindestens die Vorderseite).', dniSaveFailedError: 'Dein Dokument konnte nicht gespeichert werden. Bitte versuche es erneut.',
    scanFilled: (fields) => `Aus dem Ausweis übernommen: ${fields}. Bitte vor dem Senden prüfen.`,
    scanKept: 'Was du bereits eingetragen hattest, haben wir nicht geändert. Im Ausweis steht:', scanUse: 'Übernehmen',
    firstName: 'Vorname *', lastName: 'Nachname *', dniNie: 'Ausweis / Reisepass', birthDate: 'Geburtsdatum', birthPlaceholder: 'tt/mm/jjjj', phone: 'Telefon', email: 'E-Mail', address: 'Adresse', city: 'Stadt', postalCode: 'PLZ',
    requiredError: 'Vor- und Nachname sind erforderlich.', submitError: 'Fehler beim Senden der Anfrage. Bitte versuche es erneut.', submitting: 'Senden…', submit: 'Anfrage senden',
    camHttps: 'Die Kamera ist nur über sichere Verbindungen (HTTPS) verfügbar. Nutze "Foto hochladen", um ein Bild auszuwählen.',
    camNoSupport: 'Dein Browser erlaubt keinen Kamerazugriff. Nutze "Foto hochladen", um ein Bild auszuwählen.',
    camDenied: 'Kein Zugriff auf die Kamera. Prüfe, ob der Browser die Berechtigung hat.',
    camHttp: 'Die Kamera ist über HTTP nicht verfügbar. Nutze "Foto hochladen", um ein Bild auszuwählen.',
    cameraNotReady: 'Die Kamera ist noch nicht bereit. Warte kurz und versuche es erneut.',
    cameraCaptureFailed: 'Das Foto konnte nicht gespeichert werden. Versuche es erneut oder nutze "Foto hochladen".',
    crashTitle: 'Es ist ein Fehler aufgetreten',
    crashDesc: 'Deine eingegebenen Daten wurden gespeichert. Bitte versuche es erneut — bereits aufgenommene Fotos müssen wiederholt werden.',
    crashRetry: 'Erneut versuchen',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Das Dokument konnte nicht gelesen werden. Bitte fülle die Angaben von Hand aus.', infoErrorTitle: 'Das Formular konnte nicht geladen werden', infoErrorDesc: 'Prüfe deine Verbindung und versuche es erneut.', reintentar: 'Erneut versuchen',
    esperandoLectura: 'Warte, bis das Lesen des Dokuments abgeschlossen ist…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Wird hochgeladen… ${p}%`, subidaOk: '✓ Hochgeladen', cambiar: 'Ändern',
    esperandoSubida: { foto: 'Warten, bis das Mitgliedsfoto hochgeladen ist…', dni_anverso: 'Warten, bis die Vorderseite des Dokuments hochgeladen ist…', dni_reverso: 'Warten, bis die Rückseite des Dokuments hochgeladen ist…', firma: 'Warten, bis die Unterschrift hochgeladen ist…' },
    errSubida: { red: 'Hochladen fehlgeschlagen. Prüfe deine Verbindung und tippe auf „Erneut versuchen“.', tipo: 'Diese Datei ist kein gültiges Bild. Mach oder wähle ein anderes.', grande: 'Das Foto ist zu groß. Mach oder wähle ein anderes.', vacio: 'Die Datei ist leer. Mach oder wähle ein anderes Foto.', sinArchivo: 'Dieses Foto muss neu aufgenommen werden.', otro: 'Hochladen fehlgeschlagen. Mach oder wähle ein anderes Foto.', firma: 'Die Unterschrift konnte nicht gespeichert werden. Bitte unterschreibe erneut.' },
    revisaCampo: (c) => `Prüfe das Feld ${c}`, quitar: 'Entfernen',
    bloqueoReintentar: 'Ein Foto konnte nicht hochgeladen werden. Tippe auf „Erneut versuchen“.', bloqueoRehacer: 'Ein Bild konnte nicht hochgeladen werden. Mach das Foto neu.',
    bloqueoRehacerOQuitar: 'Ein Bild konnte nicht hochgeladen werden. Mach das Foto neu oder entferne es.',
    envioGrande: 'Die Übermittlung ist zu groß. Bitte versuche es erneut.', envioReintentar: 'Die Übermittlung konnte nicht abgeschlossen werden. Bitte versuche es erneut.', borradorElige: 'Wähle eine Option, um fortzufahren.',
    noHaLlegado: 'Deine Anfrage ist nicht angekommen. Prüfe deine Verbindung und versuche es erneut.', reintentandoEnvio: 'Keine Antwort vom Server. Ich prüfe und versuche es erneut…', fotoCaducada: 'Eines der Fotos ist nicht mehr gültig. Mach es neu und sende erneut.',
    borradorAviso: 'Auf diesem Gerät gibt es eine unvollständige Anfrage.', continuarDe: (n) => (n ? `Anfrage von ${n} fortsetzen` : 'Anfrage fortsetzen'), empezarNuevo: 'Neu beginnen',
  },
  pt: {
    abrirAparte: 'Abrir o documento noutro separador', heLeido: 'Li o documento e concordo', faltaLeer: 'Marca «Li o documento» para poderes assinar.', faltaFirmar: 'Falta a tua assinatura no quadro.', irAFirmar: 'Ir assinar ↓', avisoMovil: 'Se o documento não aparecer aqui, abre-o noutro separador com a ligação acima e volta para assinar: assina-se no quadro em baixo, não sobre o PDF.', descargarDocumento: 'Transferir o documento para o ler', avisoSinVisor: 'Este navegador não consegue mostrar o PDF aqui. Transfere-o, lê-o a partir da notificação de transferência e volta: assina-se no quadro em baixo, não sobre o PDF.',
    actaTitulo: 'Ata de inscrição', firmar: 'Assinar documento', firmado: '✓ Documento assinado', verOFirmar: 'Ver ou assinar de novo', actaSub: 'Lê o documento e assina no final.', actaCargando: 'A carregar o documento…', actaError: 'Não foi possível carregar o documento. Sem documento não se pode assinar.', tuFirma: 'A tua assinatura', firmaAyuda: 'Assina com o dedo dentro do quadrado.', firmarYCerrar: 'Assinar e fechar', cerrarSinFirmar: 'Fechar sem assinar',
    linkInvalid: 'Link inválido',
    linkInvalidDesc: 'Este link de pré-inscrição não existe ou expirou.',
    sentTitle: 'Pedido enviado!',
    sentDesc: (name) => `O teu pedido de pré-inscrição foi recebido por ${name}. O clube irá analisá-lo e entrará em contacto contigo.`,
    subtitle: 'Formulário de pré-inscrição de sócio',
    poweredBy: 'Powered by',
    photoLabel: 'Foto do sócio', capture: 'Capturar', cancel: 'Cancelar', takeSelfie: 'Tirar selfie', uploadPhoto: 'Carregar foto', retry: 'Repetir',
    scanDni: 'Digitalizar documento (opcional — preenche o formulário automaticamente)', front: 'Frente', back: 'Verso', dniReadError: 'Não foi possível ler o documento.',
    scanDniRequired: 'Digitalizar documento * (obrigatório — preenche o formulário automaticamente)', photoRequiredError: 'Este clube exige a foto do sócio para a pré-inscrição.', dniRequiredError: 'Este clube exige a digitalização do documento (pelo menos a frente).', dniSaveFailedError: 'Não foi possível guardar o documento. Tenta novamente.',
    scanFilled: (fields) => `Preenchido a partir do documento: ${fields}. Confere antes de enviar.`,
    scanKept: 'Não alterámos o que já tinhas escrito. O documento diz:', scanUse: 'Usar',
    firstName: 'Nome *', lastName: 'Apelidos *', dniNie: 'Documento / passaporte', birthDate: 'Data de nascimento', birthPlaceholder: 'dd/mm/aaaa', phone: 'Telefone', email: 'Email', address: 'Morada', city: 'Cidade', postalCode: 'Código postal',
    requiredError: 'Nome e apelidos são obrigatórios.', submitError: 'Erro ao enviar o pedido. Tenta novamente.', submitting: 'A enviar…', submit: 'Enviar pedido',
    camHttps: 'A câmara só está disponível em ligações seguras (HTTPS). Usa "Carregar foto" para selecionar uma imagem.',
    camNoSupport: 'O teu navegador não permite o acesso à câmara. Usa "Carregar foto" para selecionar uma imagem.',
    camDenied: 'Não foi possível aceder à câmara. Verifica se o navegador tem permissão.',
    camHttp: 'A câmara não está disponível em HTTP. Usa "Carregar foto" para selecionar uma imagem.',
    cameraNotReady: 'A câmara ainda não está pronta. Aguarda um segundo e tenta de novo.',
    cameraCaptureFailed: 'Não foi possível guardar a foto. Tenta de novo ou usa "Carregar foto".',
    crashTitle: 'Ocorreu um erro',
    crashDesc: 'Guardámos os dados que tinhas escrito. Tenta de novo — as fotos já tiradas terão de ser repetidas.',
    crashRetry: 'Tentar de novo',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Não foi possível ler o documento. Preenche os dados à mão.', infoErrorTitle: 'Não foi possível carregar o formulário', infoErrorDesc: 'Verifica a tua ligação e tenta novamente.', reintentar: 'Tentar de novo',
    esperandoLectura: 'A aguardar que a leitura do documento termine…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `A enviar… ${p}%`, subidaOk: '✓ Enviada', cambiar: 'Mudar',
    esperandoSubida: { foto: 'A aguardar que a foto do sócio seja enviada…', dni_anverso: 'A aguardar que a frente do documento seja enviada…', dni_reverso: 'A aguardar que o verso do documento seja enviado…', firma: 'A aguardar que a assinatura seja enviada…' },
    errSubida: { red: 'Não foi possível enviar. Verifica a tua ligação e toca em Tentar de novo.', tipo: 'Este ficheiro não é uma imagem válida. Tira ou escolhe outra.', grande: 'A foto é demasiado pesada. Tira ou escolhe outra.', vacio: 'O ficheiro está vazio. Tira ou escolhe outra foto.', sinArchivo: 'Esta foto tem de ser tirada outra vez.', otro: 'Não foi possível enviar. Tira ou escolhe outra foto.', firma: 'Não foi possível guardar a assinatura. Assina outra vez.' },
    revisaCampo: (c) => `Verifica o campo ${c}`, quitar: 'Remover',
    bloqueoReintentar: 'Uma foto não foi enviada. Toca em «Tentar de novo».', bloqueoRehacer: 'Uma imagem não foi enviada. Tira a foto outra vez.',
    bloqueoRehacerOQuitar: 'Uma imagem não foi enviada. Tira a foto outra vez ou remove-a.',
    envioGrande: 'O envio é demasiado grande. Tenta de novo.', envioReintentar: 'Não foi possível concluir o envio. Tenta de novo.', borradorElige: 'Escolhe uma opção para continuar.',
    noHaLlegado: 'O teu pedido não chegou. Verifica a tua ligação e tenta de novo.', reintentandoEnvio: 'Sem resposta do servidor. A verificar e a tentar de novo…', fotoCaducada: 'Uma das fotos já não é válida. Tira-a outra vez e envia de novo.',
    borradorAviso: 'Há um pedido por terminar neste dispositivo.', continuarDe: (n) => (n ? `Continuar o pedido de ${n}` : 'Continuar o pedido'), empezarNuevo: 'Começar de novo',
  },
  fr: {
    abrirAparte: 'Ouvrir le document dans un nouvel onglet', heLeido: "J'ai lu le document et je l'accepte", faltaLeer: 'Cochez «J’ai lu le document» pour pouvoir signer.', faltaFirmar: 'Il manque votre signature dans le cadre.', irAFirmar: 'Aller signer ↓', avisoMovil: "Si le document ne s'affiche pas ici, ouvrez-le dans un nouvel onglet avec le lien ci-dessus et revenez pour signer : la signature se fait dans le cadre ci-dessous, pas sur le PDF.", descargarDocumento: 'Télécharger le document pour le lire', avisoSinVisor: "Ce navigateur ne peut pas afficher le PDF ici. Téléchargez-le, lisez-le depuis la notification de téléchargement et revenez : la signature se fait dans le cadre ci-dessous, pas sur le PDF.",
    actaTitulo: "Bulletin d'adhésion", firmar: 'Signer le document', firmado: '✓ Document signé', verOFirmar: 'Voir ou signer à nouveau', actaSub: 'Lisez le document et signez à la fin.', actaCargando: 'Chargement du document…', actaError: "Le document n'a pas pu être chargé. Sans document, impossible de signer.", tuFirma: 'Votre signature', firmaAyuda: 'Signez avec le doigt dans le cadre.', firmarYCerrar: 'Signer et fermer', cerrarSinFirmar: 'Fermer sans signer',
    linkInvalid: 'Lien non valide',
    linkInvalidDesc: "Ce lien de préinscription n'existe pas ou a expiré.",
    sentTitle: 'Demande envoyée !',
    sentDesc: (name) => `Votre demande de préinscription a bien été reçue par ${name}. Le club va l'examiner et vous contactera.`,
    subtitle: 'Formulaire de préinscription de membre',
    poweredBy: 'Powered by',
    photoLabel: 'Photo du membre', capture: 'Capturer', cancel: 'Annuler', takeSelfie: 'Prendre un selfie', uploadPhoto: 'Importer une photo', retry: 'Reprendre',
    scanDni: "Scanner la pièce d'identité (facultatif — remplit le formulaire automatiquement)", front: 'Recto', back: 'Verso', dniReadError: "Impossible de lire la pièce d'identité.",
    scanDniRequired: "Scanner la pièce d'identité * (obligatoire — remplit le formulaire automatiquement)", photoRequiredError: 'Ce club exige une photo du membre pour la préinscription.', dniRequiredError: "Ce club exige le scan de la pièce d'identité (au moins le recto).", dniSaveFailedError: "Le document n'a pas pu être enregistré. Veuillez réessayer.",
    scanFilled: (fields) => `Rempli depuis votre pièce d'identité : ${fields}. Vérifiez avant d'envoyer.`,
    scanKept: "Nous n'avons pas modifié ce que vous aviez déjà saisi. Votre pièce d'identité indique :", scanUse: 'Utiliser',
    firstName: 'Prénom *', lastName: 'Nom *', dniNie: "Pièce d'identité / passeport", birthDate: 'Date de naissance', birthPlaceholder: 'jj/mm/aaaa', phone: 'Téléphone', email: 'E-mail', address: 'Adresse', city: 'Ville', postalCode: 'Code postal',
    requiredError: 'Le prénom et le nom sont obligatoires.', submitError: "Erreur lors de l'envoi de la demande. Veuillez réessayer.", submitting: 'Envoi…', submit: 'Envoyer la demande',
    camHttps: 'La caméra n’est disponible que sur une connexion sécurisée (HTTPS). Utilisez « Importer une photo » pour choisir une image.',
    camNoSupport: "Votre navigateur ne permet pas d'accéder à la caméra. Utilisez « Importer une photo » pour choisir une image.",
    camDenied: "Impossible d'accéder à la caméra. Vérifiez que le navigateur en a l'autorisation.",
    camHttp: "La caméra n'est pas disponible en HTTP. Utilisez « Importer une photo » pour choisir une image.",
    cameraNotReady: "La caméra n'est pas encore prête. Attendez une seconde et réessayez.",
    cameraCaptureFailed: "La photo n'a pas pu être enregistrée. Réessayez ou utilisez « Importer une photo ».",
    crashTitle: 'Une erreur est survenue',
    crashDesc: 'Nous avons conservé les informations saisies. Réessayez — les photos déjà prises devront être refaites.',
    crashRetry: 'Réessayer',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Impossible de lire le document. Remplissez les données à la main.', infoErrorTitle: "Le formulaire n'a pas pu être chargé", infoErrorDesc: 'Vérifiez votre connexion et réessayez.', reintentar: 'Réessayer',
    esperandoLectura: 'En attente de la fin de la lecture du document…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Envoi… ${p} %`, subidaOk: '✓ Envoyée', cambiar: 'Changer',
    esperandoSubida: { foto: "En attente de l'envoi de la photo du membre…", dni_anverso: "En attente de l'envoi du recto du document…", dni_reverso: "En attente de l'envoi du verso du document…", firma: "En attente de l'envoi de la signature…" },
    errSubida: { red: "Envoi impossible. Vérifiez votre connexion et touchez Réessayer.", tipo: "Ce fichier n'est pas une image valide. Prenez ou choisissez-en une autre.", grande: "La photo est trop lourde. Prenez ou choisissez-en une autre.", vacio: "Le fichier est vide. Prenez ou choisissez une autre photo.", sinArchivo: "Cette photo doit être reprise.", otro: "Envoi impossible. Prenez ou choisissez une autre photo.", firma: "La signature n'a pas pu être enregistrée. Signez à nouveau." },
    revisaCampo: (c) => `Vérifiez le champ ${c}`, quitar: 'Retirer',
    bloqueoReintentar: 'Une photo n\'a pas pu être envoyée. Touchez « Réessayer ».', bloqueoRehacer: 'Une image n\'a pas pu être envoyée. Reprenez la photo.',
    bloqueoRehacerOQuitar: 'Une image n\'a pas pu être envoyée. Reprenez la photo ou retirez-la.',
    envioGrande: 'L\'envoi est trop volumineux. Réessayez.', envioReintentar: 'L\'envoi n\'a pas pu être terminé. Réessayez.', borradorElige: 'Choisissez une option pour continuer.',
    noHaLlegado: "Votre demande n'est pas arrivée. Vérifiez votre connexion et réessayez.", reintentandoEnvio: "Pas de réponse du serveur. Vérification et nouvel essai…", fotoCaducada: "L'une des photos n'est plus valide. Reprenez-la et renvoyez.",
    borradorAviso: "Une demande non terminée existe sur cet appareil.", continuarDe: (n) => (n ? `Continuer la demande de ${n}` : 'Continuer la demande'), empezarNuevo: 'Recommencer',
  },
  nl: {
    abrirAparte: 'Document openen in een nieuw tabblad', heLeido: 'Ik heb het document gelezen en ga akkoord', faltaLeer: 'Vink «Ik heb het document gelezen» aan om te kunnen ondertekenen.', faltaFirmar: 'Je handtekening in het vak ontbreekt.', irAFirmar: 'Naar ondertekenen ↓', avisoMovil: 'Zie je het document hier niet? Open het in een nieuw tabblad met de link hierboven en kom terug om te ondertekenen: je tekent in het vak hieronder, niet op de pdf.', descargarDocumento: 'Document downloaden om te lezen', avisoSinVisor: 'Deze browser kan de pdf hier niet tonen. Download hem, lees hem via de downloadmelding en kom terug: je tekent in het vak hieronder, niet op de pdf.',
    actaTitulo: 'Inschrijvingsformulier', firmar: 'Document ondertekenen', firmado: '✓ Document ondertekend', verOFirmar: 'Bekijken of opnieuw ondertekenen', actaSub: 'Lees het document en teken onderaan.', actaCargando: 'Document laden…', actaError: 'Het document kon niet worden geladen. Zonder document kun je niet ondertekenen.', tuFirma: 'Je handtekening', firmaAyuda: 'Teken met je vinger in het vak.', firmarYCerrar: 'Ondertekenen en sluiten', cerrarSinFirmar: 'Sluiten zonder te ondertekenen',
    linkInvalid: 'Ongeldige link',
    linkInvalidDesc: 'Deze link voor voorinschrijving bestaat niet of is verlopen.',
    sentTitle: 'Aanvraag verstuurd!',
    sentDesc: (name) => `Je aanvraag voor voorinschrijving is ontvangen door ${name}. De club bekijkt hem en neemt contact met je op.`,
    subtitle: 'Voorinschrijvingsformulier voor leden',
    poweredBy: 'Powered by',
    photoLabel: 'Foto van het lid', capture: 'Vastleggen', cancel: 'Annuleren', takeSelfie: 'Selfie maken', uploadPhoto: 'Foto uploaden', retry: 'Opnieuw',
    scanDni: 'ID-bewijs scannen (optioneel — vult het formulier automatisch in)', front: 'Voorkant', back: 'Achterkant', dniReadError: 'Het ID-bewijs kon niet worden gelezen.',
    scanDniRequired: 'ID-bewijs scannen * (verplicht — vult het formulier automatisch in)', photoRequiredError: 'Deze club vraagt een foto van het lid voor de voorinschrijving.', dniRequiredError: 'Deze club vraagt een scan van je ID-bewijs (minstens de voorkant).', dniSaveFailedError: 'Je document kon niet worden opgeslagen. Probeer het opnieuw.',
    scanFilled: (fields) => `Ingevuld vanuit je ID-bewijs: ${fields}. Controleer het voor je verstuurt.`,
    scanKept: 'Wat je al had ingevuld, hebben we niet veranderd. Op je ID-bewijs staat:', scanUse: 'Gebruiken',
    firstName: 'Voornaam *', lastName: 'Achternaam *', dniNie: 'ID-bewijs / paspoort', birthDate: 'Geboortedatum', birthPlaceholder: 'dd/mm/jjjj', phone: 'Telefoon', email: 'E-mail', address: 'Adres', city: 'Woonplaats', postalCode: 'Postcode',
    requiredError: 'Voornaam en achternaam zijn verplicht.', submitError: 'Fout bij het versturen van de aanvraag. Probeer het opnieuw.', submitting: 'Versturen…', submit: 'Aanvraag versturen',
    camHttps: 'De camera werkt alleen via een beveiligde verbinding (HTTPS). Gebruik "Foto uploaden" om een afbeelding te kiezen.',
    camNoSupport: 'Je browser geeft geen toegang tot de camera. Gebruik "Foto uploaden" om een afbeelding te kiezen.',
    camDenied: 'Geen toegang tot de camera. Controleer of de browser toestemming heeft.',
    camHttp: 'De camera werkt niet via HTTP. Gebruik "Foto uploaden" om een afbeelding te kiezen.',
    cameraNotReady: 'De camera is nog niet klaar. Wacht een seconde en probeer het opnieuw.',
    cameraCaptureFailed: 'De foto kon niet worden opgeslagen. Probeer het opnieuw of gebruik "Foto uploaden".',
    crashTitle: 'Er is iets misgegaan',
    crashDesc: 'We hebben de gegevens die je had ingevuld bewaard. Probeer het opnieuw — foto’s die je al had gemaakt, moet je opnieuw maken.',
    crashRetry: 'Opnieuw proberen',
    // Esperas con salida (RF-025): nada gira para siempre.
    dniTimeout: 'Het document kon niet worden gelezen. Vul de gegevens handmatig in.', infoErrorTitle: 'Het formulier kon niet worden geladen', infoErrorDesc: 'Controleer je verbinding en probeer het opnieuw.', reintentar: 'Opnieuw proberen',
    esperandoLectura: 'Wachten tot het lezen van het document klaar is…',
    // Subida por partes (RF-020..RF-024): cada foto se sube al hacerla.
    subiendoPct: (p) => `Uploaden… ${p}%`, subidaOk: '✓ Geüpload', cambiar: 'Wijzigen',
    esperandoSubida: { foto: 'Wachten tot de foto van het lid is geüpload…', dni_anverso: 'Wachten tot de voorkant van het document is geüpload…', dni_reverso: 'Wachten tot de achterkant van het document is geüpload…', firma: 'Wachten tot de handtekening is geüpload…' },
    errSubida: { red: 'Uploaden mislukt. Controleer je verbinding en tik op Opnieuw proberen.', tipo: 'Dit bestand is geen geldige afbeelding. Maak of kies een andere.', grande: 'De foto is te groot. Maak of kies een andere.', vacio: 'Het bestand is leeg. Maak of kies een andere foto.', sinArchivo: 'Deze foto moet opnieuw worden gemaakt.', otro: 'Uploaden mislukt. Maak of kies een andere foto.', firma: 'De handtekening kon niet worden opgeslagen. Onderteken opnieuw.' },
    revisaCampo: (c) => `Controleer het veld ${c}`, quitar: 'Verwijderen',
    bloqueoReintentar: 'Een foto kon niet worden geüpload. Tik op “Opnieuw proberen”.', bloqueoRehacer: 'Een afbeelding kon niet worden geüpload. Maak de foto opnieuw.',
    bloqueoRehacerOQuitar: 'Een afbeelding kon niet worden geüpload. Maak de foto opnieuw of verwijder hem.',
    envioGrande: 'De verzending is te groot. Probeer het opnieuw.', envioReintentar: 'De verzending kon niet worden voltooid. Probeer het opnieuw.', borradorElige: 'Kies een optie om verder te gaan.',
    noHaLlegado: 'Je aanvraag is niet aangekomen. Controleer je verbinding en probeer het opnieuw.', reintentandoEnvio: 'Geen antwoord van de server. Controleren en opnieuw proberen…', fotoCaducada: 'Een van de foto’s is niet meer geldig. Maak hem opnieuw en verstuur opnieuw.',
    borradorAviso: 'Er staat een onafgemaakte aanvraag op dit apparaat.', continuarDe: (n) => (n ? `Aanvraag van ${n} voortzetten` : 'Aanvraag voortzetten'), empezarNuevo: 'Opnieuw beginnen',
  },
};

export function detectLang() {
  const nav = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'es';
  const code = nav.slice(0, 2).toLowerCase();
  return STRINGS[code] ? code : 'es';
}

// Selector de idioma: bandera encima del código. Compacto, para la cabecera.
function LangSelector({ lang, setLang }) {
  return (
    <div className="prf-langs">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          title={l.label}
          aria-label={l.label}
          aria-pressed={lang === l.code}
          className={`prf-lang${lang === l.code ? ' is-active' : ''}`}
        >
          <span className="prf-lang-flag" aria-hidden="true">
            <img src={l.flag} alt="" aria-hidden="true" width="24" height="16" data-testid={`flag-${l.code}`} />
          </span>
          <span>{l.code.toUpperCase()}</span>
        </button>
      ))}
    </div>
  );
}

// ── VenueLogo — shows venue image, falls back to coloured initial ─────────────
function VenueLogo({ name, imagePath }) {
  const [imgError, setImgError] = React.useState(false);
  const initial = (name || '?')[0].toUpperCase();

  if (imagePath && !imgError) {
    return (
      <img
        src={getImageUrl(imagePath)}
        alt={name}
        onError={() => setImgError(true)}
        className="prf-logo"
      />
    );
  }
  return <div className="prf-logo prf-logo--fallback" aria-hidden="true">{initial}</div>;
}


// El formulario se rellena de pie, en el móvil y con prisa. Si la página se cae
// (o el socio recarga sin querer), perder todo lo tecleado es lo que convierte
// un fallo en un abandono. Guardamos solo los campos de texto: los ficheros no
// caben en sessionStorage y hay que rehacerlos.
const DRAFT_PREFIX = 'prereg-draft:';

// La tablet del club la usa un socio detrás de otro. Un borrador viejo en
// pantalla sería enseñarle a alguien los datos del anterior, así que caduca:
// media hora cubre de sobra rellenar el formulario, y quien lo abandona no se
// lo deja al siguiente. (Mismo motivo por el que #330 decidió no persistir la
// idempotency key entre recargas.)
const DRAFT_TTL_MS = 30 * 60 * 1000;

export function readDraft(token) {
  try {
    const raw = sessionStorage.getItem(DRAFT_PREFIX + token);
    if (!raw) return {};
    const { savedAt, ...draft } = JSON.parse(raw);
    if (!savedAt || Date.now() - savedAt > DRAFT_TTL_MS) {
      sessionStorage.removeItem(DRAFT_PREFIX + token);
      return {};
    }
    return draft;
  } catch {
    return {};   // incógnito o almacenamiento lleno: el borrador es un extra
  }
}

// Campo del backend (`SolicitudIn`) → clave de su etiqueta en STRINGS.
const ETIQUETA_DE_CAMPO = {
  first_name: 'firstName', last_name: 'lastName', dni: 'dniNie', birth_date: 'birthDate',
  phone: 'phone', email: 'email', address: 'address', city: 'city', postal_code: 'postalCode',
};

// Qué se le dice al socio cuando /solicitud contesta con un 4xx. SOLO se pinta un `detail`
// de la lista blanca (códigos estables del backend y del proxy): nunca una cadena cruda
// ni, con un 422 de pydantic, una lista. Todo lo demás cae al genérico.
function mensajeDeSolicitud(status, data, t) {
  const detalle = data?.detail;
  if (Array.isArray(detalle)) {
    const loc = detalle[0]?.loc;
    const campo = Array.isArray(loc) ? ETIQUETA_DE_CAMPO[loc[loc.length - 1]] : null;
    return campo ? t.revisaCampo((t[campo] || '').replace('*', '').trim()) : t.submitError;
  }
  if (status === 404) return t.linkInvalidDesc;
  const porCodigo = {
    photo_required: t.photoRequiredError,
    dni_required: t.dniRequiredError,
    dni_save_failed: t.dniSaveFailedError,
    length_required: t.envioReintentar,
    payload_too_large: t.envioGrande,
    upload_invalid: t.fotoCaducada,   // sin `kind` válido: no se sabe cuál, que rehaga la foto
  };
  return (typeof detalle === 'string' && Object.prototype.hasOwnProperty.call(porCodigo, detalle)
    && porCodigo[detalle]) || t.submitError;
}

function clearDraft(token) {
  try { sessionStorage.removeItem(DRAFT_PREFIX + token); } catch { /* noop */ }
}

// Solo las imágenes que ya están en el servidor: la firma NO se restaura. Su ref no
// dice contra qué datos se firmó y `caducaAlCambiar` ya no podría invalidarla si el
// socio edita algo tras recargar: es más seguro (y cuesta un segundo) volver a firmar.
const REFS_EN_BORRADOR = ['foto', 'dni_anverso', 'dni_reverso'];
function refsDelBorrador(draft) {
  const out = {};
  REFS_EN_BORRADOR.forEach((k) => {
    if (typeof draft?.refs?.[k] === 'string' && draft.refs[k]) out[k] = draft.refs[k];
  });
  return out;
}

// Estado de UN hueco de imagen (RF-020): progreso, «✓ subida» o error con «Reintentar».
// Un mensaje de error no se cierra solo: se queda hasta que el socio actúa.
function EstadoSubida({ kind, slot, t, etiqueta = '', onReintentar, onCambiar, onQuitar }) {
  if (slot.estado === 'vacio') return null;
  if (slot.estado === 'subiendo') {
    return (
      <p className="prf-upload" role="status" data-testid={`subida-${kind}`}>
        <span>{etiqueta}{t.subiendoPct(slot.pct)}</span>
        <span className="prf-upload-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={slot.pct}>
          <span style={{ width: `${slot.pct}%` }} />
        </span>
      </p>
    );
  }
  if (slot.estado === 'subida') {
    return (
      <p className="prf-upload prf-upload--ok" role="status" data-testid={`subida-${kind}`}>
        <span>{etiqueta}{t.subidaOk}</span>
        {slot.restaurada && onCambiar && (
          <button type="button" className="prf-btn-link" onClick={onCambiar}>{t.cambiar}</button>
        )}
      </p>
    );
  }
  const textos = t.errSubida;
  const msg = kind === 'firma' && slot.codigo !== 'red'
    ? textos.firma
    : (textos[slot.codigo] || textos.otro);
  return (
    <p className="prf-hint" role="alert" data-testid={`subida-${kind}`}>
      <span>{etiqueta}{msg}</span>
      {slot.codigo === 'red' && (
        <button type="button" className="prf-btn-link" onClick={onReintentar}>{t.reintentar}</button>
      )}
      {onQuitar && (
        <button type="button" className="prf-btn-link" onClick={onQuitar}>{t.quitar}</button>
      )}
    </p>
  );
}

// ── component ─────────────────────────────────────────────────────────────────
export default function PreRegisterPage() {
  const { venueToken } = useParams();

  // Borrador de una sesión anterior (recarga o caída de la página): se lee una
  // sola vez, en el primer render.
  const draft = useRef(readDraft(venueToken)).current;

  // Idioma del formulario público (ES/EN/IT/DE/PT/FR/NL). Por defecto, el del navegador.
  const [lang, setLang] = useState(() => draft.lang || detectLang());
  const t = STRINGS[lang] || STRINGS.es;
  // Firma del acta hecha aquí, en el móvil del socio. Viaja con la solicitud y el
  // club la recoge al aprobar el alta: así no hay que volver a molestarle.
  // (ACTA_FIRMA_PLAN.md F3)
  const [signature, setSignature] = useState(null);

  // Imágenes ya subidas en una sesión anterior (recarga): solo las refs, nunca los File.
  const refsRestauradas = useRef(refsDelBorrador(draft)).current;
  const subidas = usePreinscripcionSubidas(venueToken, refsRestauradas);
  const { subir, reintentar, descartar, reiniciar } = subidas;
  // Una clave por formulario. Si hay borrador, es la suya (RF-023): recargar con la
  // solicitud en el aire y reenviar no debe crear un duplicado.
  const idempotencyKey = useRef(null);
  if (idempotencyKey.current === null) idempotencyKey.current = draft.key || newIdempotencyKey();
  // «Se intentó enviar»: ya hay una solicitud en el aire con esa clave.
  const [intentado, setIntentado] = useState(Boolean(draft.intentado));
  // Un borrador con fotos subidas o ya enviado NO se reanuda en silencio: en una tablet
  // compartida lo siguiente que verías son los datos del socio anterior (#330).
  const [reanudar, setReanudar] = useState(
    () => Object.keys(refsRestauradas).length > 0 || Boolean(draft.intentado),
  );
  const nombreDelBorrador = useRef(
    [draft.firstName, draft.lastName].filter(Boolean).join(' ').trim(),
  ).current;
  // Tras enviar con éxito el borrador se borra y no debe reescribirse.
  const enviadaRef = useRef(false);

  // El documento declaraba `lang="en"` fijo mientras servía castellano, así que
  // Chrome ofrecía traducir; al hacerlo sustituye los nodos de texto por sus
  // <font> y React revienta con removeChild (crash de prod del 06-08-2026).
  // Declaramos el idioma real y marcamos la página como no-traducible: el
  // formulario ya tiene su propio selector, la traducción automática sobra.
  useEffect(() => {
    const root = document.documentElement;
    const prevLang = root.lang;
    root.lang = lang;
    root.setAttribute('translate', 'no');
    root.classList.add('notranslate');
    return () => {
      root.lang = prevLang;
      root.removeAttribute('translate');
      root.classList.remove('notranslate');
    };
  }, [lang]);

  const [venueInfo, setVenueInfo]       = useState(null);
  const [notFound, setNotFound]         = useState(false);
  // Fallo de red/timeout al cargar el club: NO es «enlace no válido» (eso es un 404).
  // `infoIntento` sube al pulsar «Reintentar» y relanza el GET.
  const [infoError, setInfoError]       = useState(false);
  const [infoIntento, setInfoIntento]   = useState(0);

  // form fields
  const [firstName,  setFirstName]  = useState(draft.firstName || '');
  const [lastName,   setLastName]   = useState(draft.lastName  || '');
  const [dni,        setDni]        = useState(draft.dni       || '');
  const [birthDate,  setBirthDate]  = useState(draft.birthDate || '');   // dd/mm/yyyy display
  const [birthISO,   setBirthISO]   = useState(draft.birthISO  || '');
  const [phone,      setPhone]      = useState(draft.phone     || '');
  const [email,      setEmail]      = useState(draft.email     || '');
  const [address,    setAddress]    = useState(draft.address   || '');
  // Ciudad y CP (decidido 22-09-2026): sin ellos, todo socio por QR llegaba al alta
  // sin ciudad. El anverso del DNI no los trae; el reverso solo la ciudad.
  const [city,       setCity]       = useState(draft.city      || '');
  const [postalCode, setPostalCode] = useState(draft.postalCode || '');

  // Persistir el borrador para sobrevivir a una recarga o a un crash.
  // Además de los textos: la clave de idempotencia y las REFS de lo ya subido (RF-023),
  // pero nunca los ficheros.
  const refFoto = subidas.refs.foto;
  const refAnverso = subidas.refs.dni_anverso;
  const refReverso = subidas.refs.dni_reverso;
  useEffect(() => {
    if (enviadaRef.current) return;
    try {
      const refs = {};
      if (refFoto) refs.foto = refFoto;
      if (refAnverso) refs.dni_anverso = refAnverso;
      if (refReverso) refs.dni_reverso = refReverso;
      sessionStorage.setItem(DRAFT_PREFIX + venueToken, JSON.stringify({
        firstName, lastName, dni, birthDate, birthISO, phone, email, address, city, postalCode, lang,
        key: idempotencyKey.current, refs, intentado,
        savedAt: Date.now(),
      }));
    } catch {
      /* almacenamiento lleno o bloqueado: seguir sin borrador */
    }
  }, [venueToken, firstName, lastName, dni, birthDate, birthISO, phone, email, address, city, postalCode, lang,
    refFoto, refAnverso, refReverso, intentado]);

  // files
  // Los ficheros viven en el hook de subidas (ya subidos o por reintentar); aquí solo las
  // miniaturas locales.
  const [photoUrl,  setPhotoUrl]  = useState(null);
  // Cada miniatura es un object URL que retiene la foto en memoria: se revoca al
  // reemplazarla, quitarla, empezar de nuevo y al salir.
  const urlsRef = useRef({});
  useEffect(() => {
    const urls = urlsRef.current;
    return () => Object.values(urls).forEach((u) => { if (u) URL.revokeObjectURL(u); });
  }, []);
  const [fotoPreparando, setFotoPreparando] = useState(false);   // comprimiendo la de galería
  const [dniFrontUrl, setDniFrontUrl] = useState(null);
  const [dniBackUrl,  setDniBackUrl]  = useState(null);

  // camera / scanning
  const [cameraOpen,  setCameraOpen]  = useState(false);   // selfie camera
  const [dniScanning, setDniScanning] = useState(null);    // null | 'front' | 'back'
  const [dniError,    setDniError]    = useState('');
  const [dniPartial,  setDniPartial]  = useState(null);
  const [dniFilled,    setDniFilled]    = useState([]);   // campos rellenados por el escaneo
  const [dniConflicts, setDniConflicts] = useState([]);   // lo que propone y NO ha pisado

  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const photoInputRef = useRef(null);
  const dniCamFrontInputRef = useRef(null);
  const dniCamBackInputRef = useRef(null);
  // Lo que escribió el escaneo, campo a campo: el reverso puede corregir al anverso,
  // pero lo que ha tecleado el socio no se sobrescribe nunca.
  const dniScanWrittenRef = useRef({});
  const dniScanDataRef    = useRef(null);

  // ui
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');
  // Sin respuesta al enviar: se pregunta al servidor y se reenvía solo. Esto lo dice.
  const [reenviando, setReenviando] = useState(false);
  // Doble envío: `submitting` llega con el repintado; esto, al instante (Enter repetido).
  const enviandoRef = useRef(false);
  const montadoRef = useRef(true);
  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  // La firma sale del lienzo como data URL; se sube como PNG en cuanto se firma. Si deja
  // de valer (los datos cambiaron tras firmar, `caducaAlCambiar`) o se vuelve a firmar,
  // su ref se descarta o se sustituye: una ref vieja NUNCA debe llegar al envío.
  useEffect(() => {
    if (!signature) { descartar('firma'); return; }
    subir('firma', dataUrlABlob(signature) || new Blob([], { type: 'image/png' }));
  }, [signature, subir, descartar]);

  // ── fetch venue info ────────────────────────────────────────────────────────
  useEffect(() => {
    setInfoError(false);
    // Cada intento lleva su propio cierre: una respuesta tardía del intento anterior
    // (o de otro token) no puede pisar el estado del que acaba de empezar.
    let vigente = true;
    // Con el móvil en un túnel o con la señal a medias, el GET se quedaba colgado y
    // el socio veía el spinner para siempre: 15 s y se le ofrece reintentar.
    axios.get(`/v1/pre-register/${venueToken}/info`, { timeout: INFO_TIMEOUT_MS })
      .then(r => { if (vigente) setVenueInfo(r.data); })
      .catch((err) => {
        if (!vigente) return;
        // Reintentar solo sirve si el fallo es del camino (sin respuesta, 5xx, 408,
        // 429). Un 404/410/403/422 es determinista: repetirlo da lo mismo, y el
        // socio se quedaría en un bucle en vez de saber que el enlace no vale.
        const status = err?.response?.status;
        const transitorio = !status || status >= 500 || status === 408 || status === 429;
        if (transitorio) setInfoError(true);
        else setNotFound(true);
      });
    return () => { vigente = false; };
  }, [venueToken, infoIntento]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!cameraOpen || !video || !stream) return;

    video.srcObject = stream;
    video.play?.().catch(() => {});
  }, [cameraOpen]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const ponerUrl = (kind, blob) => {
    const previa = urlsRef.current[kind];
    if (previa) URL.revokeObjectURL(previa);
    const url = blob ? URL.createObjectURL(blob) : null;
    urlsRef.current[kind] = url;
    ({ foto: setPhotoUrl, dni_anverso: setDniFrontUrl, dni_reverso: setDniBackUrl })[kind](url);
  };
  const quitarHueco = (kind) => { descartar(kind); ponerUrl(kind, null); };

  // ── camera helpers ──────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert(window.location.protocol === 'http:' ? t.camHttps : t.camNoSupport);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOpen(true);
    } catch (err) {
      const isHttps = window.location.protocol === 'https:';
      alert(isHttps ? t.camDenied : t.camHttp);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    // Si el stream aún no ha entregado un fotograma, el vídeo mide 0x0 y saldría
    // un canvas vacío (o un toBlob nulo, que reventaba la página entera).
    if (!v.videoWidth || !v.videoHeight) {
      setDniError(t.cameraNotReady);
      return;
    }
    // El lienzo se limita a MAX_IMAGE_PX: a resolución completa la selfie salía de
    // 1-2 MB y, sumada a los dos DNI, hacía que el envío no llegara.
    const scale = Math.min(1, MAX_IMAGE_PX / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    // Con JPEG y calidad explícita: sin el tipo, toBlob genera PNG (varios MB).
    c.toBlob(blob => {
      // toBlob devuelve null si el navegador no puede codificar: sin esta guarda,
      // `new File([null])` lanzaba dentro del callback y no lo capturaba nadie.
      if (!blob) {
        setDniError(t.cameraCaptureFailed);
        return;
      }
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      ponerUrl('foto', blob);
      subir('foto', file);
    }, 'image/jpeg', IMAGE_QUALITY);
    stopCamera();
  };

  // ── DNI scan ────────────────────────────────────────────────────────────────
  // Las etiquetas del formulario llevan el asterisco de obligatorio; en un aviso sobra.
  const cleanLabel = (label) => (label || '').replace('*', '').trim();

  // Campos que puede rellenar el escaneo: qué propone, qué hay ahora escrito y
  // cómo se escribe.
  const buildDniFields = (merged) => [
    { key: 'firstName', label: cleanLabel(t.firstName), text: merged.firstName, current: firstName,
      apply: () => setFirstName(merged.firstName) },
    { key: 'lastName',  label: cleanLabel(t.lastName),  text: merged.lastName,  current: lastName,
      apply: () => setLastName(merged.lastName) },
    { key: 'dni',       label: cleanLabel(t.dniNie),    text: merged.dni,       current: dni,
      apply: () => setDni(merged.dni) },
    { key: 'birthDate', label: cleanLabel(t.birthDate), current: birthDate,
      text: merged.birthDate ? toDisplay(merged.birthDate.iso) : '',
      apply: () => { setBirthDate(toDisplay(merged.birthDate.iso)); setBirthISO(merged.birthDate.iso); } },
    { key: 'address',   label: cleanLabel(t.address),   text: merged.address,   current: address,
      apply: () => setAddress(merged.address) },
    { key: 'city',      label: cleanLabel(t.city),      text: merged.city,      current: city,
      apply: () => setCity(merged.city) },
  ];

  const applyDniData = (data, existing = {}) => {
    if (!data) return;
    const merged = fusionarEscaneoDni(existing, data);
    dniScanDataRef.current = merged;
    const filled = [];
    const conflicts = [];
    buildDniFields(merged).forEach((field) => {
      if (!field.text) return;
      const current = (field.current || '').trim();
      // Un documento mal leído puede devolver cualquier cosa: si el socio ya ha
      // escrito algo en el campo, se propone, no se pisa.
      if (current && current !== dniScanWrittenRef.current[field.key]) {
        if (current !== field.text) conflicts.push({ key: field.key, label: field.label, text: field.text });
        return;
      }
      if (current === field.text) return;
      field.apply();
      dniScanWrittenRef.current[field.key] = field.text;
      filled.push(field.label);
    });
    setDniFilled(filled);
    setDniConflicts(conflicts);
  };

  // «Usar»: el escaneo solo escribe encima de lo tecleado si lo pide el socio.
  const applyDniSuggestion = (key) => {
    const merged = dniScanDataRef.current;
    if (!merged) return;
    const field = buildDniFields(merged).find((f) => f.key === key);
    if (!field || !field.text) return;
    field.apply();
    dniScanWrittenRef.current[key] = field.text;
    setDniConflicts((cs) => cs.filter((c) => c.key !== key));
    setDniFilled((f) => (f.includes(field.label) ? f : [...f, field.label]));
  };

  const processDni = async (file, side) => {
    setDniScanning(side); setDniError('');
    setDniFilled([]); setDniConflicts([]);
    let result;
    try {
      result = await scanDni(file, side, '', venueToken);
    } catch (err) {
      // `scanDni` no debería lanzar, pero si lo hace el botón de Enviar no puede
      // quedarse bloqueado «esperando la lectura».
      result = { success: false, error: '' };
    } finally {
      setDniScanning(null);
    }
    if (result.data) {
      if (side === 'front') {
        setDniPartial(result.data);
        applyDniData(result.data, {});
      } else {
        applyDniData(result.data, dniPartial || {});
      }
    }
    if (!result.success) {
      // Timeout del escáner: mensaje propio, el socio sigue a mano. Cualquier otro
      // error (incluido el `detail` del backend) se enseña tal cual.
      setDniError(result.error === 'timeout' ? t.dniTimeout : (result.error || t.dniReadError));
    }
  };

  const openDniScanner = (side) => {
    setDniError('');
    if (side === 'front') dniCamFrontInputRef.current?.click();
    else dniCamBackInputRef.current?.click();
  };

  const handleDniDeviceCapture = async (e, side) => {
    const original = e.target.files?.[0];
    e.target.value = '';
    if (!original) return;
    // El móvil entrega 5-10 MB por foto; con las tres el envío no llegaba nunca.
    setDniScanning(side);   // decodificar y codificar tarda en móviles lentos: ya se ve el spinner
    // Con tope de 20 s (RF-025): pasado, se sigue con el original.
    const { upload: file, scan } = await prepareDniPhotosConTope(original);
    ponerUrl(side === 'front' ? 'dni_anverso' : 'dni_reverso', file);
    // Sube la copia de 1600 px en paralelo al escaneo (que lee la de 2800 px).
    subir(side === 'front' ? 'dni_anverso' : 'dni_reverso', file);
    await processDni(scan, side);
  };

  // Obligatoriedad por club (petición Kong 02-09): la barrera real es el
  // backend (422); aquí se corta antes y se explica en el idioma del socio.
  const fotoObligatoria = Boolean(venueInfo?.photo_required);
  const escaneoObligatorio = Boolean(venueInfo?.dni_required);

  // Por qué no se puede enviar todavía (RF-024). Un control desactivado dice qué falta.
  // Solo el anverso es exigible: pasaportes y permisos de una cara no tienen reverso.
  const { slots } = subidas;
  // Un hueco opcional en error se puede quitar; uno obligatorio solo se repite. La firma
  // no se quita: se vuelve a firmar.
  const esOpcional = (k) => (k === 'foto' ? !fotoObligatoria : k === 'dni_anverso' ? !escaneoObligatorio : k === 'dni_reverso');
  const motivoBloqueo = (() => {
    if (fotoPreparando) return t.esperandoSubida.foto;
    const enCurso = subidas.subiendo[0];
    if (enCurso) return t.esperandoSubida[enCurso];
    // Una imagen que no se pudo subir se perdería en silencio si se enviara sin ella.
    // El texto dice solo lo que hay en pantalla: «Reintentar» únicamente si el hueco lo tiene.
    if (subidas.conError.length > 0) {
      const k = subidas.conError[0];
      if (slots[k].codigo === 'red') return t.bloqueoReintentar;
      if (k === 'firma') return t.errSubida.firma;
      return esOpcional(k) ? t.bloqueoRehacerOQuitar : t.bloqueoRehacer;
    }
    if (fotoObligatoria && slots.foto.estado !== 'subida') return t.photoRequiredError;
    if (escaneoObligatorio && slots.dni_anverso.estado !== 'subida') return t.dniRequiredError;
    return '';
  })();

  // ── submit ──────────────────────────────────────────────────────────────────
  const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Los campos de texto, tal como los mandaba el multipart: recortados y los vacíos
  // se omiten. Las imágenes ya no viajan: solo sus referencias.
  const cuerpoSolicitud = () => {
    const body = { first_name: firstName.trim(), last_name: lastName.trim() };
    if (dni)        body.dni         = dni.trim();
    if (birthISO)   body.birth_date  = birthISO;
    if (phone)      body.phone       = phone.trim();
    if (email)      body.email       = email.trim();
    if (address)    body.address     = address.trim();
    if (city)       body.city        = city.trim();
    if (postalCode) body.postal_code = postalCode.trim();
    body.refs = subidas.obtenerRefs();
    return body;
  };

  // ¿Llegó la solicitud con esta clave? (RF-022). Un fallo al preguntar cuenta como «no».
  const llegoLaSolicitud = async () => {
    try {
      const r = await axios.get(
        `/v1/pre-register/${venueToken}/solicitud/${encodeURIComponent(idempotencyKey.current)}`,
        { timeout: ESTADO_TIMEOUT_MS },
      );
      return r?.data?.recibida === true;
    } catch {
      return false;
    }
  };

  // Envía y se ocupa de todo lo recuperable sin molestar al socio. Devuelve
  // `{ ok: true }`, `{ error: texto }` o `{ cancelado: true }` (se desmontó).
  const enviarSolicitud = async () => {
    let reenvios = 0;
    const resubidas = new Set();   // cada hueco se vuelve a subir UNA vez
    for (;;) {
      try {
        await axios.post(`/v1/pre-register/${venueToken}/solicitud`, cuerpoSolicitud(), {
          headers: { 'Idempotency-Key': idempotencyKey.current },
          timeout: SOLICITUD_TIMEOUT_MS,
        });
        return { ok: true };
      } catch (err) {
        if (!montadoRef.current) return { cancelado: true };
        const status = err?.response?.status;
        const data = err?.response?.data;

        // El servidor ya no acepta una referencia (caducó, o la clave de firma rotó):
        // `kind` dice cuál. Se vuelve a subir desde el fichero guardado y se reenvía.
        if (status === 422 && data?.detail === 'upload_invalid' && KINDS.includes(data.kind)) {
          if (resubidas.has(data.kind)) return { error: t.fotoCaducada };
          resubidas.add(data.kind);
          // Sin fichero (viene de un borrador tras recargar) el hueco queda en error
          // pidiendo la foto otra vez.
          const ref = await reintentar(data.kind);
          if (!montadoRef.current) return { cancelado: true };
          if (!ref) return { error: t.fotoCaducada };
          continue;
        }

        // Sin respuesta, timeout o 5xx: no sabemos si llegó. Se pregunta; si no, se
        // reenvía con LA MISMA clave (si llegó entretanto, el servidor devuelve la ficha).
        const transitorio = !status || status >= 500 || status === 408 || status === 429;
        if (transitorio) {
          if (await llegoLaSolicitud()) return { ok: true };
          if (!montadoRef.current) return { cancelado: true };
          if (reenvios >= MAX_REENVIOS) return { error: t.noHaLlegado };
          setReenviando(true);
          await esperar(TIEMPOS.esperaBaseMs * 2 ** reenvios);
          reenvios += 1;
          if (!montadoRef.current) return { cancelado: true };
          continue;
        }

        // El backend habla en códigos estables (p. ej. si el club encendió la
        // exigencia DESPUÉS de cargar este formulario); aquí se traducen.
        return { error: mensajeDeSolicitud(status, data, t) };
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // The button is disabled, but Enter inside a text field still submits the form.
    if (enviandoRef.current || submitting || dniScanning || reanudar || motivoBloqueo) return;
    if (!firstName.trim() || !lastName.trim()) {
      setError(t.requiredError);
      return;
    }
    enviandoRef.current = true;
    setSubmitting(true); setError('');
    setIntentado(true);    // desde aquí hay una solicitud en el aire con esta clave
    try {
      const res = await enviarSolicitud();
      if (res.cancelado) return;
      if (res.ok) {
        // Enviada: el borrador ya no sirve y no debe llegarle al siguiente socio
        // que use la misma tablet.
        enviadaRef.current = true;
        clearDraft(venueToken);
        setSubmitted(true);
      } else {
        setError(res.error);
      }
    } finally {
      enviandoRef.current = false;
      if (montadoRef.current) { setSubmitting(false); setReenviando(false); }
    }
  };

  // «Empezar de nuevo»: la solicitud restaurada no es de quien tiene el móvil/tablet.
  // Fuera borrador, clave, referencias y textos; el siguiente envío es otra solicitud.
  const empezarDeNuevo = () => {
    clearDraft(venueToken);
    idempotencyKey.current = newIdempotencyKey();
    enviadaRef.current = false;
    reiniciar();
    setSignature(null);
    [setFirstName, setLastName, setDni, setBirthDate, setBirthISO, setPhone, setEmail,
      setAddress, setCity, setPostalCode].forEach((set) => set(''));
    ponerUrl('foto', null); ponerUrl('dni_anverso', null); ponerUrl('dni_reverso', null);
    setDniError(''); setDniPartial(null); setDniFilled([]); setDniConflicts([]);
    dniScanWrittenRef.current = {};
    dniScanDataRef.current = null;
    setError('');
    setIntentado(false);
    setReanudar(false);
  };

  // ── render states ────────────────────────────────────────────────────────────
  if (notFound) return (
    <div className="prf-state">
      <div className="prf-state-langs"><LangSelector lang={lang} setLang={setLang} /></div>
      <div className="prf-state-card">
        <LinkIcon size={40} className="prf-state-icon--error" aria-hidden="true" />
        <h1 className="prf-state-title">{t.linkInvalid}</h1>
        <p className="prf-state-body">{t.linkInvalidDesc}</p>
      </div>
    </div>
  );

  if (infoError) return (
    <div className="prf-state">
      <div className="prf-state-langs"><LangSelector lang={lang} setLang={setLang} /></div>
      <div className="prf-state-card" role="alert">
        <h1 className="prf-state-title">{t.infoErrorTitle}</h1>
        <p className="prf-state-body">{t.infoErrorDesc}</p>
        <button
          type="button"
          className="prf-btn prf-btn--primary"
          onClick={() => { setInfoError(false); setInfoIntento((n) => n + 1); }}
        >
          {t.reintentar}
        </button>
      </div>
    </div>
  );

  if (!venueInfo) return (
    <div className="prf-state">
      <span className="prf-spinner" role="status" aria-label="…" />
    </div>
  );

  if (submitted) return (
    <div className="prf-state">
      <div className="prf-state-card">
        <CheckCircle2 size={40} className="prf-state-icon--ok" aria-hidden="true" />
        <h1 className="prf-state-title">{t.sentTitle}</h1>
        <p className="prf-state-body">{t.sentDesc(venueInfo.name)}</p>
      </div>
    </div>
  );

  return (
    <div className="prf-page">
      <div className="prf-wrap">

        {/* ── cabecera ── */}
        <header className="prf-card prf-head">
          <div className="prf-head-row">
            <VenueLogo name={venueInfo.name} imagePath={venueInfo.image_path} />
            <div className="prf-head-text">
              <h1 className="prf-club">{venueInfo.name}</h1>
              <p className="prf-subtitle">{t.subtitle}</p>
            </div>
          </div>
          <div className="prf-head-bottom">
            <LangSelector lang={lang} setLang={setLang} />
            <p className="prf-powered">{t.poweredBy} ClubsAgent</p>
          </div>
        </header>

        {reanudar && (
          <div className="prf-card prf-resume" role="status">
            <p className="prf-resume-text">{t.borradorAviso} {t.borradorElige}</p>
            <div className="prf-row">
              <button type="button" className="prf-btn prf-btn--primary" onClick={() => setReanudar(false)}>
                {t.continuarDe(nombreDelBorrador)}
              </button>
              <button type="button" className="prf-btn" onClick={empezarDeNuevo}>{t.empezarNuevo}</button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="prf-form">
         {/* Con el aviso de borrador visible el formulario está inerte: en una tablet
             compartida, otro socio podría pisar los datos del anterior y enviar con SU clave. */}
         <fieldset disabled={reanudar} className="prf-fieldset">
          <div className="prf-card prf-fields">

            {/* ── foto del socio ── */}
            <div className="prf-photo">
              {photoUrl
                ? <img src={photoUrl} alt={t.photoLabel} className="prf-avatar" />
                : (
                  <div className="prf-avatar prf-avatar--empty" aria-hidden="true">
                    <UserRound size={32} />
                  </div>
                )}
              <div className="prf-photo-col">
                <span className="prf-label">{t.photoLabel}{fotoObligatoria ? ' *' : ''}</span>
                {cameraOpen ? (
                  <>
                    <video ref={videoRef} autoPlay playsInline muted className="prf-video" />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    <div className="prf-row">
                      <button type="button" className="prf-btn prf-btn--primary" onClick={capturePhoto}>
                        <Camera size={16} aria-hidden="true" /> {t.capture}
                      </button>
                      <button type="button" className="prf-btn" onClick={stopCamera}>{t.cancel}</button>
                    </div>
                  </>
                ) : (
                  <div className="prf-row">
                    <button type="button" className="prf-btn prf-btn--primary" onClick={startCamera}>
                      <Camera size={16} aria-hidden="true" /> {t.takeSelfie}
                    </button>
                    <label className="prf-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={16} aria-hidden="true" /> {t.uploadPhoto}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async e => {
                          const original = e.target.files?.[0];
                          e.target.value = '';
                          if (!original) return;
                          // Misma compresión que la selfie de cámara: una foto de
                          // galería puede pesar 8 MB y tumbar el envío.
                          setFotoPreparando(true);
                          let f;
                          try {
                            f = await compressImageConTope(original);
                          } finally {
                            setFotoPreparando(false);
                          }
                          ponerUrl('foto', f);
                          subir('foto', f);
                        }}
                      />
                    </label>
                    {photoUrl && (
                      <button type="button" className="prf-btn-link" style={{ flex: '0 0 auto' }} onClick={startCamera}>
                        {t.retry}
                      </button>
                    )}
                  </div>
                )}
                <EstadoSubida
                  kind="foto"
                  slot={slots.foto}
                  t={t}
                  onReintentar={() => reintentar('foto')}
                  onCambiar={startCamera}
                  onQuitar={esOpcional('foto') ? () => quitarHueco('foto') : undefined}
                />
              </div>
            </div>

            {/* ── escanear DNI ── */}
            <div className="prf-field">
              <span className="prf-label">{escaneoObligatorio ? t.scanDniRequired : t.scanDni}</span>
              <div className="prf-row">
                <button type="button" className="prf-btn" onClick={() => openDniScanner('front')}>
                  {dniScanning === 'front'
                    ? <span className="prf-spinner" />
                    : <><ScanLine size={16} aria-hidden="true" /> {t.front}</>}
                </button>
                <button type="button" className="prf-btn" onClick={() => openDniScanner('back')}>
                  {dniScanning === 'back'
                    ? <span className="prf-spinner" />
                    : <><ScanLine size={16} aria-hidden="true" /> {t.back}</>}
                </button>
              </div>
              <EstadoSubida
                kind="dni_anverso"
                slot={slots.dni_anverso}
                t={t}
                etiqueta={`${t.front}: `}
                onReintentar={() => reintentar('dni_anverso')}
                onCambiar={() => openDniScanner('front')}
                onQuitar={esOpcional('dni_anverso') ? () => quitarHueco('dni_anverso') : undefined}
              />
              <EstadoSubida
                kind="dni_reverso"
                slot={slots.dni_reverso}
                t={t}
                etiqueta={`${t.back}: `}
                onReintentar={() => reintentar('dni_reverso')}
                onCambiar={() => openDniScanner('back')}
                onQuitar={esOpcional('dni_reverso') ? () => quitarHueco('dni_reverso') : undefined}
              />
              {dniError && <p className="prf-hint">{dniError}</p>}
              {dniFilled.length > 0 && <p className="prf-scan-note">{t.scanFilled(dniFilled.join(', '))}</p>}
              {dniConflicts.length > 0 && (
                <div className="prf-scan-kept">
                  <span>{t.scanKept}</span>
                  {dniConflicts.map((c) => (
                    <span key={c.key} className="prf-scan-kept-row">
                      <span>{c.label}: <strong>{c.text}</strong></span>
                      <button type="button" className="prf-btn-link" onClick={() => applyDniSuggestion(c.key)}>{t.scanUse}</button>
                    </span>
                  ))}
                </div>
              )}
              {(dniFrontUrl || dniBackUrl) && (
                <div className="prf-dni-shots">
                  {dniFrontUrl && <img src={dniFrontUrl} alt={t.front} />}
                  {dniBackUrl && <img src={dniBackUrl} alt={t.back} />}
                </div>
              )}
            </div>

            {/* ── datos ── */}
            <div className="prf-row">
              <label className="prf-field">
                <span className="prf-field-label">{t.firstName}</span>
                <input className="prf-input" value={firstName} maxLength={200} onChange={e => setFirstName(e.target.value)} required />
              </label>
              <label className="prf-field">
                <span className="prf-field-label">{t.lastName}</span>
                <input className="prf-input" value={lastName} maxLength={200} onChange={e => setLastName(e.target.value)} required />
              </label>
            </div>

            <div className="prf-row">
              <label className="prf-field">
                <span className="prf-field-label">{t.dniNie}</span>
                <input className="prf-input" value={dni} maxLength={20} onChange={e => setDni(e.target.value)} />
              </label>
              <label className="prf-field">
                <span className="prf-field-label">{t.birthDate}</span>
                <input
                  className="prf-input"
                  value={birthDate}
                  onChange={e => {
                    const v = fmtDate(e.target.value);
                    setBirthDate(v);
                    if (v.length === 10) setBirthISO(toISO(v));
                  }}
                  placeholder={t.birthPlaceholder}
                  maxLength={10}
                />
              </label>
            </div>

            <label className="prf-field">
              <span className="prf-field-label">{t.phone}</span>
              <input className="prf-input" type="tel" maxLength={50} value={phone} onChange={e => setPhone(e.target.value)} />
            </label>

            <label className="prf-field">
              <span className="prf-field-label">{t.email}</span>
              <input className="prf-input" type="email" maxLength={200} value={email} onChange={e => setEmail(e.target.value)} />
            </label>

            <label className="prf-field">
              <span className="prf-field-label">{t.address}</span>
              <input className="prf-input" value={address} maxLength={500} onChange={e => setAddress(e.target.value)} />
            </label>

            <div className="prf-row">
              <label className="prf-field">
                <span className="prf-field-label">{t.postalCode}</span>
                <input className="prf-input" inputMode="numeric" autoComplete="postal-code" maxLength={50} value={postalCode} onChange={e => setPostalCode(e.target.value)} />
              </label>
              <label className="prf-field">
                <span className="prf-field-label">{t.city}</span>
                <input className="prf-input" autoComplete="address-level2" maxLength={100} value={city} onChange={e => setCity(e.target.value)} />
              </label>
            </div>
          </div>

          {/* ── Acta: leerla y firmarla antes de enviar ─────────────────── */}
          <div className="prf-field">
            <FirmarDocumento
              value={signature}
              onChange={setSignature}
              datos={{ first_name: firstName, last_name: lastName, dni, email, address, phone }}
              // Aquí SÍ caduca: el socio está delante y puede seguir editando
              // (o el escáner del DNI rellenar campos) después de firmar, y
              // volver a firmar le cuesta un segundo. Lo que no caduca es esta
              // misma firma más tarde, cuando el club completa datos al aprobar
              // y el socio ya no está (decidido 02-09) — eso se decide en el
              // alta, no aquí.
              caducaAlCambiar
              // Chrome de Android no pinta PDFs en un iframe y Safari de iOS
              // enseña solo la primera página: tener el visor delante no prueba
              // que se haya leído. Se pide marcarlo, con enlace para abrirlo.
              confirmarLectura
              textos={{
                firmar: t.firmar,
                firmado: t.firmado,
                verOFirmar: t.verOFirmar,
                titulo: t.actaTitulo,
                subtitulo: t.actaSub,
                cargando: t.actaCargando,
                errorCarga: t.actaError,
                tuFirma: t.tuFirma,
                firmaAyuda: t.firmaAyuda,
                cancelar: t.cancel,
                firmarYCerrar: t.firmarYCerrar,
                cerrarSinFirmar: t.cerrarSinFirmar,
                abrirAparte: t.abrirAparte,
                descargarDocumento: t.descargarDocumento,
                avisoSinVisor: t.avisoSinVisor,
                heLeido: t.heLeido,
                faltaLeer: t.faltaLeer,
                faltaFirmar: t.faltaFirmar,
                irAFirmar: t.irAFirmar,
                avisoMovil: t.avisoMovil,
                reintentar: t.reintentar,
              }}
              pedirActa={async () => {
                const res = await axios.post(
                  `/v1/pre-register/${venueToken}/acta/preview`,
                  { first_name: firstName, last_name: lastName, dni, email, address, phone },
                  { responseType: 'blob', timeout: ACTA_TIMEOUT_MS },
                );
                return res.data;
              }}
            />
            <EstadoSubida
              kind="firma"
              slot={slots.firma}
              t={t}
              etiqueta={`${t.tuFirma}: `}
              onReintentar={() => reintentar('firma')}
            />
          </div>

          {error && <div className="prf-error" role="alert">{error}</div>}

          <button
            type="submit"
            className="prf-btn prf-btn--primary prf-btn--block"
            disabled={submitting || reanudar || Boolean(dniScanning) || Boolean(motivoBloqueo)}
            // Verde en cuanto está firmado, como en la tablet: se ve de un vistazo
            // que ya se puede enviar (petición de Antonio).
            style={signature ? { backgroundColor: '#059669', borderColor: '#059669' } : undefined}
          >
            {submitting ? t.submitting : t.submit}
          </button>
          {/* Un control desactivado dice qué falta. */}
          {dniScanning && <p className="prf-scan-note" role="status">{t.esperandoLectura}</p>}
          {!dniScanning && !submitting && motivoBloqueo && (
            <p className="prf-scan-note" role="status">{motivoBloqueo}</p>
          )}
          {reenviando && submitting && <p className="prf-scan-note" role="status">{t.reintentandoEnvio}</p>}
         </fieldset>
        </form>
      </div>

      {/* ── cámara nativa del DNI (oculta, la disparan Anverso/Reverso) ── */}
      <input
        ref={dniCamFrontInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleDniDeviceCapture(e, 'front')}
        style={{ display: 'none' }}
      />
      <input
        ref={dniCamBackInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleDniDeviceCapture(e, 'back')}
        style={{ display: 'none' }}
      />
    </div>
  );
}
